import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { t } = useI18n();
  const [status, setStatus] = useState("checking");
  const [msg, setMsg] = useState("");
  const attempts = useRef(0);

  const sessionId = params.get("session_id");
  const type = params.get("type") || "lifetime";

  useEffect(() => { setMsg(t("ps_checking")); }, [t]);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); setMsg(t("ps_notfound")); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setStatus("error"); setMsg(t("ps_slow")); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("success");
          setMsg(type === "super" ? t("ps_success_super") : type === "coach" ? t("ps_success_coach") : t("ps_success_life"));
          setTimeout(() => navigate("/dashboard"), 1600);
          return;
        }
        if (data.status === "expired") { setStatus("error"); setMsg(t("ps_notfound")); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, type, refresh, navigate, t]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-5">
      <div className="gt-card p-10 max-w-md w-full text-center">
        {status === "checking" && <Loader2 className="mx-auto text-[#007BFF] animate-spin mb-4" size={48} />}
        {status === "success" && <CheckCircle2 className="mx-auto text-[#00E676] text-glow-mint mb-4" size={48} />}
        {status === "error" && <XCircle className="mx-auto text-[#FF3B30] mb-4" size={48} />}
        <h2 className="font-display text-2xl font-extrabold mb-2 text-white">
          {status === "success" ? t("ps_done") : status === "error" ? t("ps_oops") : t("ps_wait_t")}
        </h2>
        <p className="text-white/50 text-sm mb-6">{msg}</p>
        {status === "error" && (
          <Button data-testid="ps-back-btn" onClick={() => navigate("/dashboard")} className="bg-[#007BFF] rounded-[12px]">{t("ps_go_dash")}</Button>
        )}
      </div>
    </div>
  );
}
